import { useTranslation } from "next-i18next";
import { IconTraderAvatarPlaceholder } from "src/components/icons-mui/trader-avatar-placeholder";
import { AVATAR_PIXEL_SIZE_UNIT, AVATAR_SIZE_MAP, IAvatarSize } from "src/components-mui/base/avatar/avatar.constants";
import { buildAvatarImageUrl, toResponsiveValue } from "src/components-mui/base/avatar/avatar.utils";
import { InitialsAvatar } from "src/components-mui/base/avatar/initials-avatar";
import { useAvatarColor } from "src/components-mui/base/avatar/use-avatar-color";
import { IWfAvatarProps, WfAvatar } from "src/components-mui/base/avatar/wf-avatar";
import { OptionalLink } from "src/components-mui/base/optional-link/optional-link";
import { TRADER_AVATAR_ICON_TEST_ID } from "src/components-mui/common/trader-avatar/trader-avatar.test-ids";
import { useGetUserName } from "src/hooks/use-get-user-name";
import { IUser } from "src/types/common";
import { getImageBaseUrl } from "src/utils/responsive-image-util";

export interface ITraderAvatarUser extends Pick<IUser, "id" | "nickName" | "firstName" | "lastName" | "isDeleted"> {
    /** `null` when the trader has no picture. Leave it out when unknown: the picture is then looked up by the trader id. */
    profilePictureUrl?: string | null;
}

export interface ITraderAvatarProps extends Omit<IWfAvatarProps, "size" | "src" | "children" | "alt"> {
    size: IAvatarSize | IAvatarSize[];
    trader: ITraderAvatarUser;
    href?: string;
    target?: string;
}

interface ITraderAvatarUrlParams {
    trader: ITraderAvatarUser;
    size: IAvatarSize | IAvatarSize[];
}

const isProfilePictureKnown = ({ trader }: Pick<ITraderAvatarUrlParams, "trader">) => trader.profilePictureUrl !== undefined;

const buildProfilePictureUrlFromTraderId = ({ traderId }: { traderId: string }) => `${getImageBaseUrl()}images/top-trader/${traderId}_profile.png`;

const getTraderAvatarUrl = ({ trader, size }: ITraderAvatarUrlParams) => {
    if (trader.isDeleted) {
        return undefined;
    }

    if (!isProfilePictureKnown({ trader })) {
        return buildAvatarImageUrl(buildProfilePictureUrlFromTraderId({ traderId: trader.id }), size);
    }

    return trader.profilePictureUrl ? buildAvatarImageUrl(trader.profilePictureUrl, size) : undefined;
};

interface IAvatarFallbackProps {
    trader: ITraderAvatarUser;
    size: IAvatarSize | IAvatarSize[];
    role?: string;
    "aria-label"?: string;
}

const AvatarFallback = ({ trader, size, ...rest }: IAvatarFallbackProps) => {
    const avatarColor = useAvatarColor(trader.id);

    if (!trader.isDeleted && trader.firstName && trader.lastName) {
        return <InitialsAvatar size={size} firstName={trader.firstName} lastName={trader.lastName} bg={avatarColor} {...rest} />;
    }

    return (
        <IconTraderAvatarPlaceholder data-test-id={TRADER_AVATAR_ICON_TEST_ID} sx={{ color: avatarColor }} aria-hidden focusable="false" {...rest} />
    );
};

export const TraderAvatar = ({ trader, size, href, target, loading = "lazy", sx, ...avatarProps }: ITraderAvatarProps) => {
    const { t } = useTranslation("common");
    const getUserName = useGetUserName();
    const displayName = getUserName(trader);
    const traderName = trader.isDeleted ? t("user-deleted") : t("user-avatar-of-x", { name: displayName });
    const dimension = toResponsiveValue(size, value => AVATAR_SIZE_MAP[value] * AVATAR_PIXEL_SIZE_UNIT);

    return (
        <OptionalLink
            href={trader.isDeleted ? undefined : href}
            target={target}
            variant="unstyled"
            aria-label={t("go-to-profile-of-x", { name: displayName })}
        >
            <WfAvatar
                sx={[{ width: dimension, height: dimension, bgcolor: "transparent" }, ...(Array.isArray(sx) ? sx : [sx])]}
                src={getTraderAvatarUrl({ trader, size })}
                loading={loading}
                skipImageVerification={isProfilePictureKnown({ trader })}
                alt={traderName}
                {...avatarProps}
            >
                <AvatarFallback trader={trader} size={size} />
            </WfAvatar>
        </OptionalLink>
    );
};
